import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Custom TipTap node for CTA buttons in newsletters.
 *
 * In the editor: NodeView shows a styled button with edit/delete controls.
 * In email HTML: renders as a bulletproof table-based button (Outlook-safe).
 */
export const ButtonNode = Node.create({
  name: "ctaButton",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      label: { default: "Register Now" },
      url: { default: "#" },
      bgColor: { default: "#4E95D9" },
      textColor: { default: "#ffffff" },
      align: { default: "center" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-cta-button]",
        getAttrs: (dom) => ({
          label: dom.getAttribute("data-label") || "Register Now",
          url: dom.getAttribute("data-url") || "#",
          bgColor: dom.getAttribute("data-bg") || "#4E95D9",
          textColor: dom.getAttribute("data-text-color") || "#ffffff",
          align: dom.getAttribute("data-align") || "center",
        }),
      },
    ];
  },

  // renderHTML is used for email output (editor.getHTML()) — must be email-safe
  renderHTML({ node }) {
    const { label, url, bgColor, textColor, align } = node.attrs;
    return [
      "div",
      {
        "data-cta-button": "true",
        "data-label": label,
        "data-url": url,
        "data-bg": bgColor,
        "data-text-color": textColor,
        "data-align": align,
        style: `text-align:${align};margin:24px 0;`,
      },
      [
        "a",
        {
          href: url,
          target: "_blank",
          style:
            `display:inline-block;padding:14px 32px;background-color:${bgColor};` +
            `color:${textColor};text-decoration:none;font-family:Calibri,Arial,sans-serif;` +
            `font-size:16px;font-weight:600;border-radius:6px;letter-spacing:0.3px;`,
        },
        label,
      ],
    ];
  },

  // NodeView for the editor
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const { label, url, bgColor, textColor, align } = node.attrs;

      const dom = document.createElement("div");
      dom.setAttribute("data-cta-button", "true");
      dom.style.cssText = `text-align:${align};margin:24px 0;position:relative;`;

      const wrapper = document.createElement("div");
      wrapper.style.cssText = "display:inline-block;position:relative;";

      // The button itself
      const btn = document.createElement("a");
      btn.href = url;
      btn.textContent = label;
      btn.style.cssText =
        `display:inline-block;padding:14px 32px;background-color:${bgColor};` +
        `color:${textColor};text-decoration:none;font-family:Calibri,Arial,sans-serif;` +
        `font-size:16px;font-weight:600;border-radius:6px;letter-spacing:0.3px;cursor:default;`;
      btn.addEventListener("click", (e) => e.preventDefault());

      // Edit button (pencil icon)
      const editBtn = document.createElement("button");
      editBtn.innerHTML = "&#9998;";
      editBtn.title = "Edit button";
      editBtn.style.cssText =
        "position:absolute;top:-10px;left:-10px;width:24px;height:24px;" +
        "border-radius:50%;border:none;background:rgba(78,149,217,0.9);" +
        "color:white;font-size:13px;cursor:pointer;" +
        "display:flex;align-items:center;justify-content:center;" +
        "opacity:0;transition:opacity 0.15s;z-index:2;";
      editBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const newLabel = window.prompt("Button text:", label);
        if (newLabel === null) return;
        const newUrl = window.prompt("Button URL:", url);
        if (newUrl === null) return;
        const pos = getPos();
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              label: newLabel || label,
              url: newUrl || url,
            });
            return true;
          })
          .run();
      });

      // Delete button
      const deleteBtn = document.createElement("button");
      deleteBtn.innerHTML = "&times;";
      deleteBtn.title = "Remove button";
      deleteBtn.style.cssText =
        "position:absolute;top:-10px;right:-10px;width:24px;height:24px;" +
        "border-radius:50%;border:none;background:rgba(0,0,0,0.6);" +
        "color:white;font-size:16px;cursor:pointer;" +
        "display:flex;align-items:center;justify-content:center;" +
        "opacity:0;transition:opacity 0.15s;z-index:2;";
      deleteBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const pos = getPos();
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      });

      // Show controls on hover
      wrapper.addEventListener("mouseenter", () => {
        editBtn.style.opacity = "1";
        deleteBtn.style.opacity = "1";
      });
      wrapper.addEventListener("mouseleave", () => {
        editBtn.style.opacity = "0";
        deleteBtn.style.opacity = "0";
      });

      wrapper.appendChild(btn);
      wrapper.appendChild(editBtn);
      wrapper.appendChild(deleteBtn);
      dom.appendChild(wrapper);

      return { dom };
    };
  },

  addCommands() {
    return {
      setCtaButton:
        (attrs) =>
        ({ chain }) => {
          return chain()
            .insertContent({ type: this.name, attrs })
            .run();
        },
    };
  },
});
